import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType, EParamChannelType } from '@unfoldrtech/portal-mic'
import {
  EParamChannelTypeGlobal,
  EReportColumnHeader,
  EReportingTimeWindow,
  IDOOHReportAudiencesTable,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { ReportingAudienceChartWrapper } from './ReportingAudienceChartWrapper'
import { ReportingAudienceTableWrapper } from './ReportingAudienceTableWrapper'

import {
  IDAReportAudiencesTable,
  TReportExportType,
  TReportingFilters,
} from '../../../../models'
import {
  AdGroupContext,
  AppContext,
  CampaignContext,
} from '../../../../models/contexts'

import {
  selectAudienceTableReportingFilters,
  setAdGroupIds,
  setCampaignIds,
  setIsReportingV3,
} from '../../../../store/reportingFilters'

import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { TDatasetWhitelistKey } from '../../models'
import { getAudienceEDatasetKeyFromProps } from '../helpers/get-audience-e-dataset-key-from-props'
import { convertEParamChannelTypeToEChannelTypeGlobal } from '../../../../utils/helpers'
import { convertEChannelTypeToEChannelTypeGlobal } from '../../../../utils/helpers/convert-e-channel-type-to-e-channel-type-global'
import {
  generateCorrelationId,
  getTimezone,
} from '../../../../utils/helpers'
import { coreAPI } from '../../../../services/coreAPI'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

type IReportAudiencesTable = IDAReportAudiencesTable | IDOOHReportAudiencesTable

/**
 * One report template per entity
 * Ex: ReportCategoriesTemplate, ReportCampaignsTemplate, etc ...
 */
export const ReportingAudienceTemplate = () => {
  const pageTitle = 'reporting.header.audiences'

  const dispatch = useDispatch()
  const intl = useIntl()

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)
  const filters = useSelector(selectAudienceTableReportingFilters)
  const channelType =
    convertEParamChannelTypeToEChannelTypeGlobal(
      filters.channelType as EParamChannelType
    ) || convertEChannelTypeToEChannelTypeGlobal(campaign.channelType)

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const campaignId = campaign?.id || -1
  const adGroupId = adGroup?.id

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<IReportAudiencesTable>()

  const [reportingFiltersIsSetup, setReportingFiltersIsSetup] = useState(false)

  const onTableDataChange = (locationsTableData: IReportAudiencesTable) => {
    setTableData(locationsTableData)
  }
  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      getAudienceEDatasetKeyFromProps(channelType, !!adGroup.id),
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPI.v2AdvertiserReporting().getAdvertiserAudiencesReportTable(
              advertiserId,
              generateCorrelationId(),
              retailerId,
              campaignId,
              adGroupId,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as unknown as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )
          : await coreAPI.v2RetailerReporting().getRetailerAudiencesReportTable(
              retailerId,
              generateCorrelationId(),
              advertiserId,
              campaignId,
              adGroupId,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(
      setCampaignIds({
        campaignIds: campaign?.id,
      })
    )
    dispatch(
      setAdGroupIds({
        adGroupIds: adGroup?.id,
      })
    )
    setReportingFiltersIsSetup(true)
  }, [campaign.id, adGroup.id])

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      {reportingFiltersIsSetup && (
        <>
          <ReportPageTemplateLayout
            title={pageTitle}
            isExportDisabled={isExporting || !tableData?.datasetKey}
            chartWrapper={<ReportingAudienceChartWrapper />}
            tableWrapper={
              <ReportingAudienceTableWrapper
                onTableDataChange={onTableDataChange}
              />
            }
            onExportData={onExportData}
          />
          <SuccessToast
            show={showExportCappedToast}
            onClose={() => setShowExportCappedToast(false)}
            translationKey="reporting.export.capped"
          />
          <ErrorToast
            show={showExportErrorToast}
            onClose={() => setShowExportErrorToast(false)}
            translationKey="reporting.export.failed"
          />
        </>
      )}
    </>
  )
}
